import type { ObjectMetadata } from '@olytic/foundation';

import { formatMoney, formatNumber } from '../../objects/format';
import { OBJECT_DEFS } from '../../objects/objectDefs';
import { asCheck, asDate, asLabel, createdByField, field, lastModifiedByField, lookup, type Row, show, text } from './kit';

const def = OBJECT_DEFS.revenue;
const money = show(formatMoney);

/** One month of a quote line's revenue and hours. Rationale: revenue.md. */
export const revenue: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Revenue schedule',
  plural: def.label,
  icon: def.icon,
  listColumns: def.columns,
  details: [
    field('period_start', 'From', { format: asDate }),
    field('period_end', 'To', { format: asDate }),
    field('quote_name', 'Quote', { link: lookup('quotes', 'quote_id') }),
    field('line_name', 'Quote Line'),
    field('product_name', 'Product', { link: lookup('products', 'product_id') }),
    field('selling_model', 'Sold as'),
    field('billing', 'Billing', { format: asLabel }),
    field('is_primary', 'Primary Quote', { format: asCheck }),
    field('opportunity_name', 'Opportunity', { link: lookup('opportunities', 'opportunity_id') }),
    field('stage', 'Stage', { format: asLabel }),
    field('probability', 'Probability', { format: show((v) => `${formatNumber(v)}%`) }),
    field('account_name', 'Account', { link: lookup('accounts', 'account_id') }),
    field('amount', 'Amount', { format: money }),
    field('weighted', 'Weighted', { format: money }),
    field('hours', 'Hours', { format: show((v) => formatNumber(v)) }),
    createdByField(),
    lastModifiedByField(),
  ],
  sections: [
    { key: 'identity', fields: ['period_start', 'period_end', 'opportunity_name', 'stage', 'probability', 'account_name'] },
    { key: 'amounts', label: 'Amounts', fields: ['amount', 'weighted', 'hours'] },
    // Where the row came from: the quote line that generated it, and what was sold.
    { key: 'source', label: 'Source', fields: ['quote_name', 'is_primary', 'line_name', 'product_name', 'selling_model', 'billing'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_by_name', 'last_modified_by_name'] },
  ],
  related: [],
  titleOf: (row) => text(row.name),
  searchKeys: ['quote_name', 'line_name', 'product_name', 'opportunity_name', 'account_name'],
};
